import { useState, useEffect } from 'react';
import { supabase, type Product, type Category, type Subcategory, type Brand } from '../lib/supabase';

export interface ProductWithCategory extends Product {
  category_info?: Category;
  subcategory_info?: Subcategory;
  brand_info?: Brand;
}

export const useProducts = (filters?: {
  category?: string;
  subcategory?: string | string[];
  brand?: string;
  productType?: string;
  featured?: boolean;
  limit?: number;
  search?: string;
}) => {
  const [products, setProducts] = useState<ProductWithCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);

        // Build the query with proper error handling
        let query = supabase
          .from('products')
          .select(`
            *,
            category_info:categories!products_category_fkey(*),
            subcategory_info:subcategories!products_subcategory_fkey(*),
            brand_info:brands!products_brand_id_fkey(*)
          `)
          .eq('status', 'active');

        // Apply filters
        if (filters?.category) {
          query = query.eq('category', filters.category);
        }

        if (filters?.subcategory) {
          if (Array.isArray(filters.subcategory)) {
            // Handle multiple subcategories (from checkbox filters)
            if (filters.subcategory.length > 0) {
              query = query.in('subcategory', filters.subcategory);
            }
          } else {
            // Handle single subcategory (from URL parameter)
            query = query.eq('subcategory', filters.subcategory);
          }
        }

        if (filters?.brand) {
          query = query.eq('brand_id', filters.brand);
        }

        if (filters?.productType) {
          query = query.eq('product_type', filters.productType);
        }

        if (filters?.featured) {
          query = query.eq('is_featured', true);
        }

        if (filters?.search) {
          query = query.ilike('name', `%${filters.search}%`);
        }

        if (filters?.limit) {
          query = query.limit(filters.limit);
        }

        // Order by featured first, then by created_at desc
        query = query.order('is_featured', { ascending: false })
                    .order('created_at', { ascending: false });

        const { data, error: fetchError } = await query;

        if (fetchError) {
          console.error('Supabase error:', fetchError);
          throw fetchError;
        }

        setProducts(data || []);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch products');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [filters?.category, filters?.subcategory, filters?.brand, filters?.productType, filters?.featured, filters?.limit, filters?.search]);

  return { products, loading, error };
};

export const useCategories = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from('categories')
          .select('*')
          .eq('is_active', true)
          .order('sort_order');

        if (fetchError) {
          console.error('Supabase error:', fetchError);
          throw fetchError;
        }

        setCategories(data || []);
      } catch (err) {
        console.error('Error fetching categories:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch categories');
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  return { categories, loading, error };
};

export const useSubcategories = (categoryId?: string) => {
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSubcategories = async () => {
      try {
        setLoading(true);
        setError(null);

        let query = supabase
          .from('subcategories')
          .select('*')
          .eq('is_active', true);

        if (categoryId) {
          query = query.eq('category_id', categoryId);
        }

        query = query.order('sort_order');

        const { data, error: fetchError } = await query;

        if (fetchError) {
          console.error('Supabase error:', fetchError);
          throw fetchError;
        }

        setSubcategories(data || []);
      } catch (err) {
        console.error('Error fetching subcategories:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch subcategories');
      } finally {
        setLoading(false);
      }
    };

    fetchSubcategories();
  }, [categoryId]);

  return { subcategories, loading, error };
};

export const useBrands = (featured?: boolean) => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        setLoading(true);
        setError(null);

        let query = supabase
          .from('brands')
          .select('*')
          .eq('is_active', true);

        if (featured) {
          query = query.eq('is_featured', true);
        }

        query = query.order('sort_order');

        const { data, error: fetchError } = await query;

        if (fetchError) {
          console.error('Supabase error:', fetchError);
          throw fetchError;
        }

        setBrands(data || []);
      } catch (err) {
        console.error('Error fetching brands:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch brands');
      } finally {
        setLoading(false);
      }
    };

    fetchBrands();
  }, [featured]);

  return { brands, loading, error };
};

export const useProduct = (productId: string) => {
  const [product, setProduct] = useState<ProductWithCategory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from('products')
          .select(`
            *,
            category_info:categories!products_category_fkey(*),
            subcategory_info:subcategories!products_subcategory_fkey(*),
            brand_info:brands!products_brand_id_fkey(*)
          `)
          .eq('id', productId)
          .eq('status', 'active')
          .single();

        if (fetchError) {
          console.error('Supabase error:', fetchError);
          throw fetchError;
        }

        setProduct(data);
      } catch (err) {
        console.error('Error fetching product:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch product');
      } finally {
        setLoading(false);
      }
    };

    if (productId) {
      fetchProduct();
    }
  }, [productId]);

  return { product, loading, error };
};